import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Linkedin, Facebook, Instagram, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS, ISO_CERTS, buildWhatsAppLink } from "../../lib/site";

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-ink text-white grain" data-testid="site-footer">
      <div className="blueprint-grid absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-[1400px] px-5 pt-20 pb-10 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <img src={COMPANY.logo} alt="PAHI" className="h-12 w-12 rounded-full object-cover" />
              <div>
                <div className="font-display text-xl font-bold">PAHI</div>
                <div className="text-[9px] uppercase tracking-[0.28em] text-steel">Technocrate Pvt. Ltd.</div>
              </div>
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
              {COMPANY.tagline} Precision sheet metal manufacturing, fabrication and machining components for global industrial partners.
            </p>
            <div className="mt-6 flex gap-3">
              {[Linkedin, Facebook, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 hover:border-brand-soft hover:text-brand-soft"
                  aria-label="Social link"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-steel">Navigate</div>
            <ul className="mt-6 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="group inline-flex items-center gap-1 text-sm text-white/70 transition-colors hover:text-white"
                  >
                    {l.label}
                    <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-steel">Certifications</div>
            <ul className="mt-6 space-y-3">
              {ISO_CERTS.map((c) => (
                <li key={c.code} className="text-sm">
                  <span className="font-display font-semibold text-white">{c.code}</span>
                  <div className="text-xs text-white/50">{c.label}</div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.24em] text-steel">Get in touch</div>
            <ul className="mt-6 space-y-4 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-soft" />
                <span>{COMPANY.address.full}</span>
              </li>
              {COMPANY.phones.map((p) => (
                <li key={p} className="flex gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-brand-soft" />
                  <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-white">{p}</a>
                </li>
              ))}
              {COMPANY.emails.map((e) => (
                <li key={e} className="flex gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-brand-soft" />
                  <a href={`mailto:${e}`} className="hover:text-white">{e}</a>
                </li>
              ))}
            </ul>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-brand-soft"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </div>

        <div className="mt-14 h-px w-full bg-white/10" />
        <div className="mt-6 flex flex-col items-start justify-between gap-3 text-xs text-white/40 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} PAHI TECHNOCRATE PVT. LTD. All rights reserved.</p>
          <p>Neemrana, Rajasthan · India</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
